import React, { useState, useCallback, useEffect, ReactElement } from 'react';
import { ISuiteHeaderProps, ISuiteHeaderStyles, FlexPanelCollections } from './SuiteHeader.types';
import {
  classNamesFunction,
  initializeIcons,
  Persona,
  PersonaSize,
  PanelType,
  getInitials,
  ThemeProvider,
  mergeStyles
} from '@fluentui/react';
import { getHeaderStyles } from './SuiteHeader.styles';
import { SuiteAppLauncher } from '@suiteux/suiteux-shell-applauncher';
import { AppPanel } from './AppPanel/AppPanel';
import { DefaultMeControlPanel } from './DefaultMeControl';
import { wafflePrimaryColor, waffleIconColor, defaultBackgroundColor } from './constants';
import { SuiteHeaderProvider } from './SuiteHeaderProvider';
import { HeaderButton } from './HeaderButton';
import { TenantBranding } from './TenantBranding/TenantBranding';
import { OverflowMenu } from './OverflowMenu/OverFlowMenu';
import { UniversalMeControl } from './UniversalMeControl';
import { AppLauncher } from './AppLauncher';
import { TenantList } from './TenantList';
import { HelpPanelContent } from './Help';
import { appTheme, getHoverColor, getWaffleFocusBorderColor, themeStyle } from './utils/theme';

const getClassNames = classNamesFunction<ISuiteHeaderProps, ISuiteHeaderStyles>();

/**
 * Stores all the flex panels and their state (opened or closed)
 */
interface IFlexPanelParams {
  statusMap: { [key in FlexPanelCollections]: boolean };
}

export const SuiteHeader: React.FC<ISuiteHeaderProps> = (props: ISuiteHeaderProps) => {
  const style = getHeaderStyles(props.theme);
  const classNames = getClassNames(style);

  initializeIcons();

  const {
    theme,
    appId,
    culture,
    localizationMap,
    userData,
    workloadId,
    navbarData,
    isOfficeAppLauncherDisabled,
    organizations,
    hasError,
    enabledApps,
    appName,
    appNameLabel,
    imageUrl,
    helpLink,
    isUniversalMeControlEnabled,
    viewAccountLink,
    evoBase,
    isMsalJsEnabled,
    onRenderComplete,
    onHeaderButtonClicked,
    onClickAppBranding,
    changeTenant,
    manageTenantControl,
    searchBox,
    onLeavePreview,
    onSwitch,
    onSwitchTo,
    onSignOut,
    extraActions = []
  } = props;

  const [flexPanelStatus, setFlexPanelStatus] = useState<IFlexPanelParams>({
    statusMap: {
      appLauncher: false,
      organizations: false,
      help: false,
      meControl: false
    }
  });

  // convert to menuButton to have aria-checked state
  const appItems = enabledApps.map(app => ({
    ...app,
    href: undefined,
    ...(app.selected ? { iconProps: { iconName: 'checkmark' } } : {}),
    isChecked: app.selected,
    onClick: () => {
      app.onClick?.();
      window.open(app.href, app.target);
    }
  }));

  const changePanelStatus = useCallback(
    (id: FlexPanelCollections, shouldOpen: boolean) => {
      const newStatusMap = { ...flexPanelStatus.statusMap };
      newStatusMap[id] = shouldOpen;
      setFlexPanelStatus({
        statusMap: newStatusMap
      });
    },
    [flexPanelStatus, setFlexPanelStatus]
  );

  const sendTelemetryOnButtonClick = useCallback(
    (id: string) => () => {
      onHeaderButtonClicked(id);
    },
    [onHeaderButtonClicked]
  );

  const openPanel = useCallback(
    (id: FlexPanelCollections) => () => {
      sendTelemetryOnButtonClick(id)();
      const newStatusMap = { ...flexPanelStatus.statusMap };
      newStatusMap[id] = true;
      setFlexPanelStatus({
        statusMap: newStatusMap
      });
    },
    [flexPanelStatus, sendTelemetryOnButtonClick]
  );

  useEffect(() => {
    onRenderComplete();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const extraActionElements = extraActions.map(
    (buttonProps, i): ReactElement =>
      buttonProps.render ? (
        buttonProps.render(buttonProps)
      ) : (
        <HeaderButton
          key={i}
          title={buttonProps.title}
          iconName={buttonProps.iconName}
          onClick={buttonProps.onClick?.bind(null, buttonProps)}
        />
      )
  );

  const clickAppBrandingHandler = useCallback(
    (event: React.MouseEvent<Element>) => {
      event.preventDefault();
      onClickAppBranding();
    },
    [onClickAppBranding]
  );

  return (
    <ThemeProvider theme={appTheme} style={themeStyle}>
      <SuiteHeaderProvider
        context={{
          workloadId,
          appId,
          culture,
          userData,
          localizationMap,
          theme
        }}
      >
        <div className={classNames.root} role="banner">
          <div className={classNames.leftRegion}>
            {isOfficeAppLauncherDisabled ? (
              <HeaderButton
                name={'fallback waffle'}
                title={localizationMap['apps']}
                className={mergeStyles(style.waffle, {
                  selectors: {
                    '& .ms-Button-flexContainer > i': {
                      fontSize: appTheme.fonts.xLargePlus.fontSize
                    }
                  }
                })}
                iconName={'Waffle'}
                onClick={openPanel(FlexPanelCollections.appLauncher)}
              />
            ) : (
              <SuiteAppLauncher
                navBarData={navbarData}
                isConsumer={false}
                disableTheming={true} // We must disable theming so that office theming won't override ours
                appBrandTheme={{
                  WaffleBackground: theme?.backgroundColor || defaultBackgroundColor,
                  HoverBackground: getHoverColor(theme?.backgroundColor || defaultBackgroundColor),
                  Primary: wafflePrimaryColor,
                  WaffleIcon: theme?.foregroundColor || waffleIconColor,
                  DefaultText: getWaffleFocusBorderColor(theme?.backgroundColor || defaultBackgroundColor) // Set the color of focus border
                }}
                culture={culture}
                workloadID={workloadId}
              />
            )}
          </div>

          <div className={classNames.centerRegion}>
            <div className={classNames.leftButtonGroup}>
              {/* Tenant branding */}
              {imageUrl && imageUrl !== '' && <TenantBranding imageUrl={imageUrl} />}

              {imageUrl && imageUrl !== '' && <div className={classNames.divider}></div>}
              <a
                title={typeof appName === 'string' ? appName : appNameLabel}
                className={mergeStyles(classNames.appBranding, {
                  fontSize: appTheme.fonts.mediumPlus.fontSize,
                  selectors: {
                    '& > h1': {
                      fontSize: appTheme.fonts.mediumPlus.fontSize
                    }
                  }
                })}
                href=""
                onClick={clickAppBrandingHandler}
              >
                {appName}
              </a>

              {/* App switcher */}
              <HeaderButton
                className={classNames.appSwitcher}
                name={'app switcher'}
                title={localizationMap['viewApps']}
                menuProps={{
                  items: appItems
                }}
                styles={{
                  root: {
                    minWidth: appTheme.fonts.xLargePlus.fontSize
                  }
                }}
                onMenuClick={sendTelemetryOnButtonClick('app switcher')}
              />
            </div>

            <div className={classNames.centerSearchGroup} role="search">
              {/* Search box */}
              {searchBox && searchBox()}
            </div>

            <div className={classNames.rightButtonGroup}>
              <div className={classNames.buttonGroupExpanded}>
                {extraActionElements}
                {/* Tenant picker */}
                <HeaderButton
                  name={'tenant picker'}
                  title={localizationMap['organizations']}
                  iconName={'Org'}
                  onClick={openPanel(FlexPanelCollections.organizations)}
                />
                {/* Help */}
                <HeaderButton
                  name={'help'}
                  title={localizationMap['helpPanelHeaderText']}
                  iconName={'Help'}
                  onClick={openPanel(FlexPanelCollections.help)}
                />
              </div>
              {/* Overflow menu */}
              <div className={classNames.buttonGroupCollapsed}>
                <OverflowMenu extraActions={extraActions} changePanelStatus={changePanelStatus} />
              </div>
            </div>
          </div>

          <div className={classNames.rightRegion}>
            {isUniversalMeControlEnabled ? (
              // Universal MeControl
              <UniversalMeControl
                isMsalJsEnabled={isMsalJsEnabled ?? false}
                evoBase={evoBase}
                onLeavePreview={onLeavePreview}
                openOrganizationPanel={changePanelStatus}
                onSwitch={onSwitch}
                onSwitchTo={onSwitchTo}
                onSignOut={onSignOut}
                viewAccountLink={viewAccountLink}
              />
            ) : (
              // Default/fallback MeControl
              <HeaderButton
                name={'fallback MeControl'}
                title={localizationMap['accountManager']}
                onClick={openPanel(FlexPanelCollections.meControl)}
              >
                <Persona
                  imageUrl={userData.imageUrl}
                  imageAlt={userData.name}
                  size={PersonaSize.size40}
                  coinSize={30}
                  hidePersonaDetails={true}
                  imageInitials={getInitials(userData.name, false)}
                />
              </HeaderButton>
            )}
          </div>
        </div>
        {/* Flex panels */}
        <AppPanel
          id={FlexPanelCollections.appLauncher}
          headerText={localizationMap['apps']}
          type={PanelType.smallFixedNear}
          isOpen={flexPanelStatus.statusMap.appLauncher}
          onDismiss={changePanelStatus}
          panelContent={<AppLauncher />}
        />
        <AppPanel
          id={FlexPanelCollections.organizations}
          headerText={localizationMap['organizations']}
          type={PanelType.smallFixedFar}
          isOpen={flexPanelStatus.statusMap.organizations}
          onRenderFooterContent={manageTenantControl}
          onDismiss={changePanelStatus}
          panelContent={
            <TenantList
              hasError={hasError}
              organizations={organizations}
              currentOrgId={userData.tenantId}
              switchTenant={changeTenant}
            />
          }
        />
        <AppPanel
          id={FlexPanelCollections.help}
          headerText={localizationMap['helpPanelHeaderText']}
          type={PanelType.smallFixedFar}
          isOpen={flexPanelStatus.statusMap.help}
          onDismiss={changePanelStatus}
          panelContent={<HelpPanelContent helpLink={helpLink} culture={culture} />}
        />

        <AppPanel
          id={FlexPanelCollections.meControl}
          headerText={''}
          type={PanelType.smallFixedFar}
          isOpen={flexPanelStatus.statusMap.meControl}
          onDismiss={changePanelStatus}
          panelContent={
            <DefaultMeControlPanel
              viewAccountLink={viewAccountLink}
              onSignOut={onSignOut}
              onLeavePreview={onLeavePreview}
            />
          }
        />
      </SuiteHeaderProvider>
    </ThemeProvider>
  );
};
